import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, Target, Users, TrendingUp } from "lucide-react";

interface TraitExplanationProps {
  strengthName: string;
  domain: string;
  fullDescription: string;
  keyCharacteristics: string[];
  howToApply: string[];
  developmentTips: string[];
  potentialBlindSpots?: string[];
}

export const TraitExplanation = ({
  strengthName,
  domain,
  fullDescription,
  keyCharacteristics,
  howToApply,
  developmentTips,
  potentialBlindSpots,
}: TraitExplanationProps) => {
  const { t } = useTranslation();

  const getDomainColor = (domain: string) => {
    switch (domain) {
      case "Strategic Thinking":
        return "bg-primary/10 text-primary border-primary/25";
      case "Executing":
        return "bg-secondary/10 text-brand-green-dark border-secondary/25";
      case "Influencing":
        return "bg-warning/10 text-warning border-warning/25";
      case "Relationship Building":
        return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
      default:
        return "bg-secondary/20 text-secondary border-secondary/30";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              {strengthName}
            </h2>
            <Badge className={getDomainColor(domain)}>{domain}</Badge>
          </div>
        </div>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {fullDescription}
        </p>
      </Card>

      {/* Key Characteristics */}
      <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Lightbulb className="w-5 h-5 text-primary" />
          </div>
          <h3 className="text-xl font-semibold">{t('trait.keyCharacteristics')}</h3>
        </div>
        <ul className="space-y-2">
          {keyCharacteristics.map((characteristic, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span className="text-muted-foreground">{characteristic}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* How to Apply */}
      <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-accent/10">
            <Target className="w-5 h-5 text-accent" />
          </div>
          <h3 className="text-xl font-semibold">{t('trait.howToApply')}</h3>
        </div>
        <ul className="space-y-3">
          {howToApply.map((application, index) => (
            <li key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </span>
              <span className="text-muted-foreground">{application}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Development Tips */}
      <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-success/10">
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
          <h3 className="text-xl font-semibold">{t('trait.developmentTips')}</h3>
        </div>
        <div className="grid gap-3">
          {developmentTips.map((tip, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border border-border bg-gradient-to-r from-success/5 to-transparent"
            >
              <p className="text-muted-foreground">{tip}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Potential Blind Spots */}
      {potentialBlindSpots && potentialBlindSpots.length > 0 && (
        <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50 border-warning/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-warning/10">
              <Users className="w-5 h-5 text-warning" />
            </div>
            <h3 className="text-xl font-semibold">{t('trait.blindSpots')}</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {t('trait.blindSpotsDesc')}
          </p>
          <ul className="space-y-2">
            {potentialBlindSpots.map((blindSpot, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-warning mt-1">⚠</span>
                <span className="text-muted-foreground">{blindSpot}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};
